import * as React from 'react'

import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { UserModel } from 'app/models/userModel'
import { Dropdown } from '@/ui/form/controls/Dropdown'

import { AdminIcon } from '@/modules/shared/components'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { Button } from '@/ui/button'
import { AdminColumn, AdminTable } from '../../shared/AdminTable'
import { RolePicker } from '../../users/components/RolePicker'
import { useQueryServiceAccountUsers } from '@/modules/shared/query-client/useServiceAccountAdmin'
import { ROUTES } from 'app/constants/routes'
import { isAdmin } from 'app/services/local/roleService'
import { BulkInviteUsersModal } from './BulkInviteUsersModal'

type Props = {
  companyId: string
  users: UserModel[]
  availableUsers: UserModel[]
  roles: RolePermissionModel[]
  onChange: (users: UserModel[]) => void
  onRemove: (user: UserModel) => void
  /** Po masowym zaproszeniu - lista użytkowników firmy do odświeżenia. */
  onInvited: () => void
  // Set for a service company: its roster is shown read-only next to the role holders.
  serviceAccountId?: string | null
}

/** `user.roles` ma wpis na każdą firmę użytkownika - liczy się tylko ta edytowana. */
export const rolesOf = (
  user: UserModel,
  companyId: string,
): RolePermissionModel[] =>
  user.roles?.find((role) => String(role.company?.id) === String(companyId))
    ?.company?.roles ?? []

const withRoles = (
  user: UserModel,
  companyId: string,
  next: RolePermissionModel[],
): UserModel => {
  const others = (user.roles ?? []).filter(
    (role) => String(role.company?.id) !== String(companyId),
  )
  const current = user.roles?.find(
    (role) => String(role.company?.id) === String(companyId),
  )
  return {
    ...user,
    roles: [
      ...others,
      { ...current, company: { ...current?.company, id: companyId, roles: next } },
    ] as UserModel['roles'],
  }
}

/**
 * Zakładka UŻYTKOWNICY w edycji firmy - lista użytkowników z rolami w tej firmie.
 */
export const CompanyUsersTab: React.FC<Props> = ({
  companyId,
  users,
  availableUsers,
  roles,
  onChange,
  onRemove,
  onInvited,
  serviceAccountId,
}) => {
  const { t } = useLocalize()
  const [addOpen, setAddOpen] = React.useState(false)
  const [bulkOpen, setBulkOpen] = React.useState(false)

  const usersToAdd = availableUsers.filter(
    (candidate) => !users.some((existing) => existing.id === candidate.id),
  )

  const toggleRole = (user: UserModel, role: RolePermissionModel) => {
    const current = rolesOf(user, companyId)
    const next = current.some((item) => item.id === role.id)
      ? current.filter((item) => item.id !== role.id)
      : [...current, role]
    onChange(
      users.map((item) =>
        item.id === user.id ? withRoles(item, companyId, next) : item,
      ),
    )
  }

  const columns: Array<AdminColumn<UserModel>> = [
    {
      key: 'user',
      header: t('administrationPanel-users.name-and-surname'),
      width: 400,
      render: (user) => `${user.firstName || '-'} ${user.surname || '-'}`,
    },
    {
      key: 'login',
      header: t('administrationPanel-users.login'),
      width: 350,
      render: (user) => user.login,
    },
    {
      key: 'roles',
      header: t('administrationPanel-users.role'),
      render: (user) => (
        <RolePicker
          available={roles}
          companyId={companyId}
          selected={rolesOf(user, companyId)}
          onToggle={(role) => toggleRole(user, role)}
        />
      ),
    },
    {
      key: 'delete',
      header: '',
      width: 58,
      align: 'center',
      render: (user) =>
        user.inherited ? null : (
          <AdminIcon
            name="delete"
            title={t('operation.delete')}
            onClick={() => onRemove(user)}
          />
        ),
    },
  ]

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-4 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
        <span className="tw-text-sm tw-font-medium tw-uppercase">
          {t('administrationPanel.users')}
        </span>
        {usersToAdd.length > 0 && (
          <div className="tw-relative">
            <Button
              variant="primary"
              mode="outline"
              size="sm"
              onClick={() => setAddOpen((open) => !open)}
            >
              <AdminIcon name="add" className="tw-h-3 tw-w-3" />
              {t('administrationPanel-companies.add-user')}
            </Button>
            {addOpen && (
              <div className="drop-wrapper tw-absolute tw-left-0 tw-top-full tw-z-20 tw-mt-1 tw-w-[500px] tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-p-2 tw-shadow-lg">
                <Dropdown
                  searchPlaceholder={t(
                    'administrationPanel-companies.find-user',
                  )}
                  placeholder={t('administrationPanel-companies.choose-user')}
                  options={usersToAdd.map((user) => ({
                    value: String(user.id),
                    label: `${user.firstName || '-'} ${user.surname || '-'}, ${user.login}`,
                    content: (
                      <span className="tw-flex tw-flex-col">
                        <span className="tw-text-primary">{user.login}</span>
                        <span>{`${user.firstName || '-'} ${user.surname || '-'}`}</span>
                      </span>
                    ),
                  }))}
                  onChange={(value: string) => {
                    const found = availableUsers.find(
                      (item) => String(item.id) === value,
                    )
                    if (!found) return

                    // Nowy użytkownik wchodzi bez ról w tej firmie; role w innych
                    // firmach zostają nietknięte.
                    onChange([...users, withRoles(found, companyId, [])])
                    setAddOpen(false)
                  }}
                />
              </div>
            )}
          </div>
        )}
        {isAdmin() && (
          <Button
            variant="primary"
            mode="outline"
            size="sm"
            onClick={() => setBulkOpen(true)}
          >
            <AdminIcon name="add" className="tw-h-3 tw-w-3" />
            Zaproś wielu
          </Button>
        )}
      </div>

      {bulkOpen && (
        <BulkInviteUsersModal
          companyId={companyId}
          roles={roles}
          onClose={() => setBulkOpen(false)}
          onDone={onInvited}
        />
      )}

      {serviceAccountId && <AccountWideAccess accountId={serviceAccountId} />}

      <AdminTable
        idPrefix="company-user"
        rows={users}
        columns={columns}
        rowId={(user) => user.id}
        selectable={false}
      />
    </div>
  )
}

/** Roster konta usługowego: dostęp do wszystkich firm pod kontem, niezależny od ról w tej firmie. */
const AccountWideAccess: React.FC<{ accountId: string }> = ({ accountId }) => {
  const { t } = useLocalize()
  const { data } = useQueryServiceAccountUsers(accountId)
  if (!data) return null

  const manageUrl = `${ROUTES.AdministrationPanel.NoCompanyTab.replace(':tab', 'serviceAccount')}?account=${accountId}`

  return (
    <div className="tw-flex-shrink-0 tw-border-b tw-border-base-300 tw-px-4 tw-py-3">
      <div className="tw-mb-1 tw-flex tw-items-center tw-gap-4">
        <span className="tw-text-sm tw-font-medium tw-uppercase">
          {t('administrationPanel-companies.account-wide-access')} ({data.length})
        </span>
        <a href={manageUrl} className="tw-text-sm tw-text-primary hover:tw-underline">
          {t('administrationPanel-companies.manage-service-account')}
        </a>
      </div>
      <p className="tw-mb-2 tw-text-xs tw-opacity-70">
        {t('administrationPanel-companies.account-wide-access-hint')}
      </p>
      {data.length > 0 && (
        <ul className="tw-flex tw-flex-col tw-gap-1">
          {data.map((user) => (
            <li key={user.userId} className="tw-flex tw-items-center tw-gap-3 tw-text-sm">
              <span className="tw-w-[400px] tw-truncate">
                {`${user.firstName || '-'} ${user.surname || '-'}`}
              </span>
              <span className="tw-w-[350px] tw-truncate">{user.login}</span>
              <span className="tw-rounded tw-bg-base-200 tw-px-2 tw-py-0.5 tw-text-xs">
                {t(
                  user.isOwner
                    ? 'administrationPanel-companies.account-wide-owner'
                    : 'administrationPanel-companies.account-wide-member',
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
